import { getCategoriesList } from '@/api/skippay/gcssCategories';
import { slugify } from '@/utils/string';
import {
	Category,
	getCategoryDescriptionAndImage,
	mapSubcategoriesWithDescriptionAndImage,
	type Slug,
	type SubCategory,
} from './utils';

export async function getSubCategoryBySlug(slug: string, level: number): Promise<SubCategory> {
	const data = await getCategoriesList();

	const categories = data.filter((category) => category.path.length === level + 1);

	const category = categories?.find(
		(category) => slugify(category.path[category.path.length - 1]) === slug
	);

	if (!category) {
		throw new Error(`Category with slug ${slug} not found`);
	}

	const categorySetting = await getCategoryDescriptionAndImage(category.id);

	const subCategories = data.filter(
		(categoryItem) =>
			categoryItem.path.length === level + 2 && categoryItem.path[level] === category.path[level]
	);

	const categoryMap = new Map<number, Category['subcategories'][number]>();

	if (subCategories) {
		for (const subCategory of subCategories) {
			categoryMap.set(subCategory.id, {
				slug: [slugify(subCategory.path[subCategory.path.length - 1])],
				title: subCategory.path[subCategory.path.length - 1],
				image: null,
			});
		}
	}

	const subcategories = await mapSubcategoriesWithDescriptionAndImage(category, categoryMap);

	return {
		...category,
		subcategories,
		name: category.path[category.path.length - 1],
		slug: slugify(category.path[category.path.length - 1]),
		paths: category.path.slice(0, category.path.length - 1).map((path) => slugify(path)),
		description: categorySetting ? categorySetting.description : null,
		image: categorySetting ? categorySetting.image : null,
	};
}

export async function getAllSubCategoriesPaths(level: number): Promise<
	Array<{
		params: {
			category: Slug;
			subcategoryLevelOne: Slug;
			subcategoryLevelTwo?: Slug;
			subcategoryLevelThree?: Slug;
			subcategoryLevelFour?: Slug;
			subcategoryLevelFive?: Slug;
			subcategoryLevelSix?: Slug;
		};
	}>
> {
	const data = await getCategoriesList();

	const categories = data.filter((category) => category.path.length === level + 1);

	const filtered = categories.map((category) => {
		return {
			category: slugify(category.path[0]),
			subCategory: category.path.slice(1, level + 1).map((subCategory) => slugify(subCategory)),
		};
	});

	const flattedCategories = filtered
		.flatMap((category) => {
			return {
				category: category.category,
				subCategory: category.subCategory,
			};
		})
		.filter(Boolean);

	return flattedCategories.map((category) => {
		return {
			params: {
				category: category.category,
				subcategoryLevelOne: category.subCategory[0],
				subcategoryLevelTwo: category.subCategory[1],
				subcategoryLevelThree: category.subCategory[2],
				subcategoryLevelFour: category.subCategory[3],
				subcategoryLevelFive: category.subCategory[4],
				subcategoryLevelSix: category.subCategory[5],
			},
		};
	});
}
